import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ScanLine, Users, ArrowRight, Zap, Mail, Printer, Building2, ShieldCheck, Check, Car } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND, adminHref } from "@/lib/brand";
import { BASIC_FEATURES, SPECIALIST_MATCH_FEATURES } from "@/lib/packages";
import { VinconexLogo } from "@/components/VinconexMark";
import { LegalLinks } from "@/components/LegalLinks";
import { Button } from "@/components/ui/button";

const STEPS = [
  { icon: QrCode, title: "Generate", text: "Every vehicle gets a unique QR window sticker synced from the dealer's live inventory." },
  { icon: ScanLine, title: "Scan", text: "Shoppers scan on the lot — pricing, CARFAX on pre-owned, payment calculator — even when the team is busy." },
  { icon: Users, title: "Match", text: "Optional Specialist Match add-on: a DISC-style check pairs the shopper with the salesperson whose style fits them." },
  { icon: Mail, title: "Deliver", text: "The lead lands in the dealer's CRM instantly as ADF XML, matched specialist included." },
];

const FEATURES = [
  { icon: Zap, title: "Auto inventory sync", text: "Sold cars drop off, new arrivals appear — hourly, daily or weekly." },
  { icon: Printer, title: "Mass sticker printing", text: "Select the whole lot or a handful, print branded stickers with Stock # and VIN." },
  { icon: ShieldCheck, title: "Dealer-branded pages", text: "Your colors, your logo, your phone number — powered by Vinconex." },
  { icon: Building2, title: "Multi-rooftop ready", text: "Every dealership is fully isolated: logins, inventory, leads and settings." },
];

export default function Landing() {
  const [dealers, setDealers] = useState([]);
  const [params] = useSearchParams();
  const soldFrom = params.get("sold");
  const soldName = params.get("vehicle");
  const soldDealerName = dealers.find((d) => d.slug === soldFrom)?.name;
  useEffect(() => { api.get("/public/dealers").then(({ data }) => setDealers(data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#05070C] text-white grid-noise">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-[#1C2233]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <VinconexLogo variant="wordmark" className="h-6 w-auto" />
          <div className="flex items-center gap-3">
            <Link to="/contact" className="text-sm text-slate-300 hover:text-white px-2 py-2 hidden sm:block" data-testid="contact-nav-link">Contact</Link>
            <Link to="/login">
              <Button variant="outline" data-testid="dealer-login-btn" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">Dealer login</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="add-dealership-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold shadow-lg shadow-blue-900/40">Add your dealership</Button>
            </Link>
          </div>
        </div>
      </header>

      {soldFrom && (
        <div data-testid="sold-banner" className="bg-[#0A8CF2]/15 border-b border-[#0A8CF2]/40">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="flex items-center gap-2"><Car className="h-4 w-4 text-[#7FC3FF]" /> {soldName ? <b>{soldName}</b> : "That vehicle"} has found a new home.</span>
            <Link to={`/d/${soldFrom}`} data-testid="sold-banner-dealer-link" className="text-[#7FC3FF] hover:text-white font-semibold inline-flex items-center gap-1">
              See what's new at {soldDealerName || "the dealership"} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(10,140,242,0.18),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0A8CF2]/30 bg-[#0A8CF2]/10 text-[#7FC3FF] text-xs font-mono-tech uppercase tracking-widest mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0A8CF2] animate-pulse" /> {BRAND.tagline}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Turn every windshield into a <span className="text-[#0A8CF2]">24/7 salesperson.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Vinconex puts a smart QR code on every vehicle. Shoppers scan to a page in your dealership's branding, get matched
              with the right product specialist, and the lead is in your CRM before they walk off the lot.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/signup">
                <Button size="lg" data-testid="hero-get-started-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold text-base h-12 px-7 shadow-lg shadow-blue-900/40 transition-all hover:-translate-y-0.5">
                  Add your dealership <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-100 h-12 px-7 text-base">Open dealer dashboard</Button>
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-5 flex justify-center">
            <VinconexLogo variant="full" className="w-full max-w-md drop-shadow-[0_0_40px_rgba(10,140,242,0.35)]" to={null} />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-10">How it works</h2>
        <div className="grid md:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="tracing-top rounded-xl border border-[#1C2233] bg-[#0B0F18] p-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono-tech text-[#0A8CF2] text-sm">0{i + 1}</span>
                <s.icon className="h-6 w-6 text-[#0A8CF2]" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">{s.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-[#1C2233] bg-[#0B0F18] p-6">
              <f.icon className="h-6 w-6 text-[#0A8CF2] mb-3" />
              <h3 className="font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="packages-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Simple packaging</h2>
            <p className="text-slate-400 mt-2 text-sm sm:text-base">Start with the basics. Add Specialist Match when you're ready to hand every scan to the right person.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#1C2233] bg-[#0B0F18] p-7" data-testid="package-basic">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-bold">Basic</h3>
              <span className="text-[10px] font-mono-tech uppercase tracking-widest px-2 py-1 rounded-full border border-emerald-700/50 bg-emerald-950/40 text-emerald-400">Included</span>
            </div>
            <p className="text-sm text-slate-400 mb-5">Everything a rooftop needs to turn windshields into leads.</p>
            <ul className="space-y-2.5">
              {BASIC_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
          </div>
          <div className="tracing-top rounded-2xl border border-[#0A8CF2]/40 bg-gradient-to-b from-[#0A8CF2]/10 to-[#0B0F18] p-7" data-testid="package-specialist-match">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-[#0A8CF2]" /> Specialist Match</h3>
              <span className="text-[10px] font-mono-tech uppercase tracking-widest px-2 py-1 rounded-full border border-[#0A8CF2]/40 bg-[#0A8CF2]/15 text-[#7FC3FF]">Add-on</span>
            </div>
            <p className="text-sm text-slate-400 mb-5">Not part of the basic package. Request it from your dashboard — Vinconex activates it after review.</p>
            <ul className="space-y-2.5">
              {SPECIALIST_MATCH_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="h-4 w-4 text-[#0A8CF2] mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {dealers.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="dealers-on-vinconex">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Dealerships on Vinconex</h2>
            <span className="font-mono-tech text-xs text-slate-500 uppercase tracking-widest">live</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dealers.map((d) => (
              <Link key={d.slug} to={`/d/${d.slug}`} data-testid={`dealer-link-${d.slug}`} className="group rounded-xl border border-[#1C2233] bg-[#0B0F18] p-5 flex items-center gap-4 hover:border-[#0A8CF2] transition-colors">
                {d.logo_url ? (
                  <img src={d.logo_url} alt={d.name} className="h-10 w-auto max-w-[120px] object-contain" />
                ) : (
                  <span className="h-10 w-10 rounded-full flex items-center justify-center font-heading font-black" style={{ background: d.accent_color }}>{d.name[0]}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-xs text-slate-500">{d.vehicle_count} vehicles live</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-[#0A8CF2]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-[#1C2233] py-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <VinconexLogo variant="wordmark" className="h-5 w-auto opacity-80" />
          <div className="flex flex-col items-center sm:items-start gap-1 text-xs text-slate-500">
            <Link to="/contact" data-testid="footer-contact-link" className="font-semibold text-slate-300 hover:text-white">Contact us</Link>
            <div className="flex items-center gap-3 whitespace-nowrap">
              <a href={BRAND.phoneHref} data-testid="footer-phone-link" className="hover:text-white">{BRAND.phone}</a>
              <span className="text-slate-700">·</span>
              <a href="mailto:contact@vinconex.com" data-testid="footer-email-link" className="hover:text-white">contact@vinconex.com</a>
            </div>
          </div>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            <LegalLinks />
            <a href={adminHref()} data-testid="footer-admin-link" className="hover:text-white">Staff</a>
          </div>
          <p className="text-xs text-slate-500 font-mono-tech uppercase tracking-widest">People + Vehicles + Opportunity</p>
          <p className="text-xs text-slate-500" data-testid="owner-line">© {new Date().getFullYear()} {BRAND.owner} · Vinconex is a {BRAND.owner} platform</p>
        </div>
      </footer>
    </div>
  );
}
